import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Phone, Mail, Users, BadgeCheck, Store, ArrowRight } from '../icons';
import { getMyBookings } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { vendorImage } from '../utils/categoryImages';

function MyBookings() {
  const { user, loading: authLoading } = useAuth();
  const { lang } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading || !user) return;
    getMyBookings()
      .then((res) => setBookings(res.data || []))
      .catch((err) => setError(err.response?.data?.error || 'Could not load your bookings.'))
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : null;

  if (authLoading) return <div className="loading-page"><div className="spinner" /></div>;

  if (!user) {
    return (
      <div className="error-page">
        <h2>{lang === 'ne' ? 'कृपया लगइन गर्नुहोस्' : 'Please log in'}</h2>
        <p style={{ color: 'var(--text-light)', marginBottom: 20 }}>
          {lang === 'ne' ? 'आफ्नो बुकिङ हेर्न लगइन गर्नुहोस्।' : 'Log in to see the vendors you have booked.'}
        </p>
        <Link to="/login" className="btn btn-primary">{lang === 'ne' ? 'लगइन' : 'Log In'}</Link>
      </div>
    );
  }

  return (
    <div className="bookings-page">
      <div className="listing-hero">
        <h1>{lang === 'ne' ? 'मेरो बुकिङ' : 'My Bookings'}</h1>
        <p>
          {lang === 'ne'
            ? 'तपाईंले बुक गर्नुभएका सेवा प्रदायक र तपाईंको विवाहमा खटिएका टोली'
            : 'The vendors you have booked, and the team assigned to your wedding'}
        </p>
      </div>

      <div className="container" style={{ padding: '40px 24px 60px' }}>
        {loading ? (
          <div className="loading-state"><div className="spinner" /></div>
        ) : error ? (
          <div className="auth-error">{error}</div>
        ) : bookings.length === 0 ? (
          <div className="bookings-empty">
            <Store size={40} />
            <h3>{lang === 'ne' ? 'अहिलेसम्म कुनै बुकिङ छैन' : 'No bookings yet'}</h3>
            <p>
              {lang === 'ne'
                ? 'तपाईंले कुनै सेवा प्रदायक बुक गरेपछि तिनीहरू यहाँ देखिनेछन्।'
                : 'Once a vendor confirms your booking, they will appear here along with the team assigned to you.'}
            </p>
            <Link to="/vendors" className="btn btn-primary">
              {lang === 'ne' ? 'सेवा प्रदायक खोज्नुहोस्' : 'Browse Vendors'} <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="bookings-list">
            {bookings.map((b) => (
              <div key={b.inquiry_id} className="booking-card">
                <div
                  className="booking-card-media"
                  style={{ backgroundImage: `url(${vendorImage(b, 400, 260)})` }}
                />

                <div className="booking-card-body">
                  <div className="booking-card-head">
                    <div>
                      <span className="booking-category">{b.category_name}</span>
                      <h3>
                        <Link to={`/vendor/${b.vendor_slug}`}>{b.business_name}</Link>
                        {b.is_verified && <BadgeCheck size={16} className="booking-verified" />}
                      </h3>
                    </div>
                    <span className="booking-status">{lang === 'ne' ? 'बुक भयो' : 'Booked'}</span>
                  </div>

                  <div className="booking-meta">
                    {b.vendor_city && <span><MapPin size={14} /> {b.vendor_city}</span>}
                    {fmtDate(b.project?.event_date || b.wedding_date) && (
                      <span><Calendar size={14} /> {fmtDate(b.project?.event_date || b.wedding_date)}</span>
                    )}
                    {b.guest_count && <span><Users size={14} /> {b.guest_count} {lang === 'ne' ? 'पाहुना' : 'guests'}</span>}
                  </div>

                  <div className="booking-contact">
                    {b.vendor_phone && <a href={`tel:${b.vendor_phone}`}><Phone size={14} /> {b.vendor_phone}</a>}
                    {b.vendor_email && <a href={`mailto:${b.vendor_email}`}><Mail size={14} /> {b.vendor_email}</a>}
                  </div>

                  <div className="booking-team">
                    <h4>{lang === 'ne' ? 'तपाईंको लागि खटिएको टोली' : 'Your assigned team'}</h4>
                    {b.assigned_team.length === 0 ? (
                      <p className="booking-team-empty">
                        {lang === 'ne'
                          ? 'यो सेवा प्रदायकले अझै टोली खटाएका छैनन्।'
                          : 'This vendor has not assigned anyone yet. They will usually confirm closer to your date.'}
                      </p>
                    ) : (
                      <ul className="booking-team-list">
                        {b.assigned_team.map((m) => (
                          <li key={m.employee_id}>
                            <span className="booking-team-avatar">{(m.name || '?').trim()[0].toUpperCase()}</span>
                            <span className="booking-team-info">
                              <strong>{m.name}</strong>
                              {m.role && <em>{m.role}</em>}
                            </span>
                            {m.phone
                              ? <a className="booking-team-phone" href={`tel:${m.phone}`}><Phone size={13} /> {m.phone}</a>
                              : m.pending && <span className="booking-team-pending">{lang === 'ne' ? 'पुष्टि हुँदै' : 'Confirming'}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyBookings;
